/**
 * Shared classes for the two recurring design-tool actions, so Save is the same
 * green and Share the same purple in every tool. These were hand-rolled per tool
 * and had drifted into green/purple/zinc variants of each other.
 *
 *   <button className={`${ACTION_BUTTON} ${hasContent ? SAVE_BUTTON : SAVE_BUTTON_DISABLED}`}>
 *   <button className={`${ACTION_BUTTON} ${copied ? SHARE_BUTTON_COPIED : SHARE_BUTTON}`}>
 *
 * Contrast is measured against the SectionCard background (bg-zinc-800/50 over
 * the zinc-900 panel = #1a2037), which is where these buttons actually sit.
 * Both pass WCAG AA for label text (4.5:1) and for non-text UI contrast (3:1):
 *
 *   Save   emerald-950 on emerald-600   6.05:1 label · 5.93:1 surface
 *   Share  purple-950  on purple-400    5.03:1 label · 4.84:1 surface
 *
 * Do not "fix" Share to purple-500 — it looks better but the label drops to
 * 4.19:1 and fails. A tinted (purple-500/20 + border) treatment fails too: the
 * border only reaches 1.99:1 against the card, so the control has no
 * identifiable boundary. Solid fills are what pass here.
 */

/**
 * Press feedback for any pressable control: a 0.97 scale while held, released on a 150ms strong
 * ease-out (.claude/skills/review-animations/STANDARDS.md). Carries the color transition too, so use it
 * INSTEAD of `transition-colors`: two transition-* utilities on one element overwrite each other.
 * Disabled buttons never match :active, so the disabled styles need no override. The `press` marker
 * opts the control out of the global touch dim in globals.css (dim + scale would stack).
 */
export const PRESS =
  'press transition-[color,background-color,border-color,opacity,scale] duration-150 ease-out active:scale-[0.97] motion-reduce:active:scale-100';

/** Shape and size shared by both. Color comes from the constants below. */
export const ACTION_BUTTON =
  'w-full px-3 py-2 text-sm font-medium rounded-lg border ' + PRESS;

/** Primary action: keep this design. */
export const SAVE_BUTTON =
  'bg-emerald-600 hover:bg-emerald-500 border-emerald-500 text-emerald-950';

/** Muted green, NOT zinc gray — a gray Save next to gray secondary buttons reads as broken. */
export const SAVE_BUTTON_DISABLED =
  'bg-emerald-600/25 border-emerald-500/40 text-emerald-100/40 cursor-not-allowed';

/** Secondary action: send this design to someone. */
export const SHARE_BUTTON =
  'bg-purple-400 hover:bg-purple-300 border-purple-300 text-purple-950';

/** Confirmation state after the link is copied. Stays purple deliberately —
 *  flipping to green would read as "saved". */
export const SHARE_BUTTON_COPIED =
  'bg-purple-300 border-purple-300 text-purple-950';

/**
 * Tertiary actions that sit under Save/Share — Export JSON, Import JSON,
 * Download, Cancel.
 *
 * The fill is deliberately near-invisible against the card (zinc-800 on a
 * zinc-800/50 card is 1.07:1), so the BORDER is the only thing marking where
 * the control is — and zinc-700 gave it just 1.29:1, failing WCAG 1.4.11's
 * 3:1 for non-text UI contrast. `border-control` is the token that fixes it
 * (3.07:1); see the note beside --color-control in globals.css.
 *
 * Label is zinc-300 rather than zinc-400: same idea, more headroom, and it
 * keeps these legible without competing with the colored primary actions.
 */
export const SECONDARY_BUTTON =
  'bg-zinc-800 hover:bg-zinc-700 border border-control text-zinc-300 ' + PRESS;

/** Secondary action at the smaller size used inside a row of two or three. */
export const SECONDARY_BUTTON_SM =
  'flex-1 px-3 py-1.5 text-xs rounded-lg ' + SECONDARY_BUTTON;

/**
 * The 16-dye palette grid, the same size in every tool (banner/shield base + layer, firework
 * colors + fades, armor dye presets, sheep wool, beacon glass). Banner used fixed 28-32px squares
 * that spread across wide gaps at 1440+, Firework `w-full` tiles that grew to ~80px: one grid,
 * capped at max-w-sm, gives ~42px swatches on desktop and ~26-30px on a 320px phone (the same size
 * as the w-7 text-color pickers). Keep the selected-state classes per tool; take the SHAPE from here:
 *
 *   <div className={DYE_SWATCH_GRID}>…<button className={`${DYE_SWATCH} border-2 …`} />
 */
export const DYE_SWATCH_GRID = 'grid grid-cols-8 gap-1.5 max-w-sm';
export const DYE_SWATCH = 'w-full aspect-square rounded-md';
